import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { PAGE_IMAGE_MAX_SIZE, PAGE_IMAGE_TYPE } from 'common/constants/files';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';
import { Banner } from 'common/components/Banner';
import { Img } from 'common/components/Img';
import CARD_PREVIEW_IMAGE from 'assets/images/training-overview-image-info.webp';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { TOfferEventFormValues } from 'offer/types';
import { FormSection } from 'common/components/FormSection';
import { Divider } from 'common/components/Divider';
import { IContentTypeValue } from 'graphql/types';
import {
    GroupConditionExtendedFilterField,
    GroupConditionFilterField,
} from 'user/forms/Field';
import { useHashMatch } from 'route/hooks/useHashMatch';
import { OfferTypeSelectField } from 'offer/forms/Field';
import {
    TextField,
    ImageField,
    ColorPickerField,
    ExtraCategoryFormFields,
} from 'common/forms/Field';

interface IProps {
    renderExtraCategoryFields?: boolean;
    renderGroupConditionFields?: boolean;
    renderTypeSelect?: boolean;
    isOfferDetailDrawer?: boolean;
    isWebinarOrMeeting?: boolean;
}

export const OfferFormFields = ({
    renderExtraCategoryFields,
    renderGroupConditionFields,
    renderTypeSelect,
    isOfferDetailDrawer,
    isWebinarOrMeeting,
}: IProps) => {
    const [translate] = useTranslation();
    const smallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.only('xs')
    );
    const isOfferSettingsUpdatePage = !!useHashMatch('OFFER_SETTINGS_EDIT');
    const isNewMeetingPage = !!useRouteMatch('OFFER_MEETING_CREATE');
    const isNewOfferPage = !!useRouteMatch('OFFER_CREATE');
    const isUpdateOfferPage = !!useRouteMatch('OFFER_EDIT');
    const isUpdateOfferDrawerPage = !!useHashMatch('OFFER_DETAIL_DRAWER');

    const { getValues, control } = useFormContext<TOfferEventFormValues>();
    const values = getValues();

    const isOfferDetailDrawerPage =
        isOfferDetailDrawer || isUpdateOfferDrawerPage;
    const isNewMeetingOrOffer = isNewMeetingPage || isNewOfferPage;

    const showColorField =
        (isNewMeetingOrOffer && values.hasDetailPage) ||
        isUpdateOfferPage ||
        isOfferSettingsUpdatePage;

    const overviewImageFields = (
        <Stack
            direction={smallScreen ? 'column-reverse' : 'row'}
            spacing={2}
            sx={{ mt: 2 }}
        >
            <Box sx={{ width: '100%' }}>
                <ImageField
                    acceptedFileTypes={PAGE_IMAGE_TYPE}
                    control={control}
                    helperText={translate('imageSizeDescription', {
                        width: IMAGE_VARIANTS[EImageVariant.Card].minWidth,
                        height: IMAGE_VARIANTS[EImageVariant.Card].minHeight,
                    })}
                    imageVariant={EImageVariant.Card}
                    label={translate('offer.overviewImage')}
                    maxUploadSize={PAGE_IMAGE_MAX_SIZE}
                    name="overviewImage"
                />
            </Box>

            <Box sx={{ width: '100%' }}>
                <Banner sx={{ overflow: 'hidden' }}>
                    <Box sx={{ pl: 3, pr: 1, py: 1 }}>
                        {translate('offer.overviewImageDescription')}
                    </Box>

                    <Box sx={{ mt: 'auto' }}>
                        <Img src={CARD_PREVIEW_IMAGE} />
                    </Box>
                </Banner>
            </Box>
        </Stack>
    );

    return (
        <Box>
            {!isUpdateOfferPage &&
                (isNewMeetingOrOffer || isOfferDetailDrawerPage) &&
                overviewImageFields}
            <Grid container spacing={2}>
                <Grid size="grow">
                    <TextField
                        control={control}
                        label={translate('offerTitle')}
                        name="title"
                    />
                </Grid>

                {showColorField && (
                    <Grid>
                        <ColorPickerField
                            control={control}
                            name="titleColor"
                            popoverPosition="bottom-end"
                            tooltip={translate('titleColor')}
                        />
                    </Grid>
                )}
            </Grid>

            {isOfferSettingsUpdatePage && (
                <TextField
                    multiline
                    control={control}
                    label={translate('description')}
                    name="description"
                />
            )}

            {isNewMeetingOrOffer && (
                <TextField
                    multiline
                    control={control}
                    label={translate('offer.descriptionBeforeSubscription')}
                    name="descriptionBeforeSubscription"
                />
            )}

            {isUpdateOfferPage && (
                <Box sx={{ mt: 2 }}>
                    <Divider />

                    <FormSection
                        renderExpanded
                        description={
                            <Box>
                                {translate(
                                    'offer.overviewPageSectionDescription'
                                )}
                            </Box>
                        }
                        title={translate('offer.overviewPage')}
                    >
                        <Box sx={{ mt: 3 }}>{overviewImageFields}</Box>

                        {renderTypeSelect && (
                            <Box sx={{ mb: 2 }}>
                                <OfferTypeSelectField
                                    control={control}
                                    name="type"
                                />
                            </Box>
                        )}
                    </FormSection>
                </Box>
            )}

            {renderExtraCategoryFields && (
                <>
                    <FormSection
                        renderExpanded
                        description={
                            <Box>
                                {translate('offer.offerExtraFieldsDescription')}
                            </Box>
                        }
                        title={translate('offer.offerExtraFields')}
                    >
                        <ExtraCategoryFormFields
                            contentType={IContentTypeValue.Offer}
                            control={control}
                            name="extraCategories"
                        />
                    </FormSection>
                </>
            )}

            {renderGroupConditionFields && (
                <FormSection
                    renderExpanded
                    description={
                        <Box>{translate('offer.groupFilterDescription')}</Box>
                    }
                    title={translate('offer.groupFilterTitle')}
                >
                    <Box sx={{ mt: 3 }}>
                        {isWebinarOrMeeting ? (
                            <GroupConditionExtendedFilterField
                                control={control}
                                name="groupConditions"
                            />
                        ) : (
                            <GroupConditionFilterField
                                control={control}
                                name="groupConditions"
                            />
                        )}
                    </Box>
                </FormSection>
            )}
        </Box>
    );
};
